import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Info, Clock } from 'lucide-react';
import { panAPI } from '../../services/api';

const inr = (v) => `₹${Math.round(v || 0).toLocaleString('en-IN')}`;

const STYLES = {
  blocked: 'border-red-200 bg-red-50 text-red-800',
  pending: 'border-blue-200 bg-blue-50 text-blue-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
};

const getBanner = (s, amount) => {
  if (!s || s.pan_status === 'verified') return null;
  const total = s.total_successful_buy_inr || 0;
  const limit = s.threshold_amount || 200000;
  const warnAt = s.threshold_warning_amount || 150000;
  const remaining = Math.max(0, limit - total);
  const crossesNow = total < limit && total + (amount || 0) >= limit;
  if (s.pan_status === 'pending' && (total >= warnAt || crossesNow)) {
    return { kind: 'pending', icon: Clock, title: 'PAN under review',
      text: total >= limit ? 'Bookings will resume once your PAN is verified.' : `You can book ${inr(remaining)} more while we verify your PAN.` };
  }
  if (total >= limit) {
    return { kind: 'blocked', icon: AlertTriangle, title: 'PAN verification required',
      text: `Your total bookings have reached ${inr(total)}. Please submit your PAN to continue booking.`, cta: true };
  }
  if (crossesNow) {
    return { kind: 'warning', icon: AlertTriangle, title: 'This booking takes you past ₹2,00,000',
      text: `Your total will be ${inr(total + amount)}. You will need to verify your PAN before your next booking.`, cta: true };
  }
  if (total >= warnAt) {
    return { kind: 'warning', icon: Info, title: 'You will need PAN soon',
      text: `You have booked ${inr(total)} so far. PAN verification is required once your total reaches ${inr(limit)} (${inr(remaining)} left).`, cta: true };
  }
  return null;
};

export const PanThresholdBanner = ({ amount }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    panAPI.getStatus().then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  const banner = getBanner(status, amount);
  if (!banner) return null;
  const Icon = banner.icon;

  return (
    <div data-testid={`pan-threshold-banner-${banner.kind}`} className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${STYLES[banner.kind]}`}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{banner.title}</p>
        <p className="text-xs mt-0.5" data-testid="pan-threshold-banner-text">{banner.text}</p>
        {banner.cta && (
          <button data-testid="pan-threshold-banner-cta" onClick={() => navigate('/kyc-new')}
            className="mt-2 text-xs font-semibold underline underline-offset-2 hover:opacity-80">
            Verify PAN now
          </button>
        )}
      </div>
    </div>
  );
};
